<template>
  <div class="login-page">
    <div class="login-wrapper">

      <!-- Logo / Brand -->
      <div class="login-brand">
        <Logo />
        <p class="login-tagline">Sistema de pedidos de comedores</p>
      </div>

      <!-- Card -->
      <div class="login-card">
        <h2 class="login-title">Iniciar sesión</h2>

        <!-- Error -->
        <div v-if="errorMsg" class="login-error">{{ errorMsg }}</div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit">
          <div class="form-field">
            <label class="form-label">Correo</label>
            <input
              v-model="email"
              type="email"
              autocomplete="email"
              required
              class="form-input"
              placeholder="usuario@empresa.com"
            />
          </div>

          <div class="form-field form-field--mb-lg">
            <label class="form-label">Contraseña</label>
            <input
              v-model="password"
              type="password"
              autocomplete="current-password"
              required
              class="form-input"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            :disabled="busy"
            class="login-btn"
            :class="{ 'login-btn--busy': busy }"
          >
            <span v-if="busy" class="login-btn__loading">Autenticando…</span>
            <template v-else>
              <Icon name="bolt" :size="15" color="#fff" />
              Entrar
            </template>
          </button>
        </form>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuth } from '../composables/useAuth.js'
import Icon from './ui/Icon.vue'
import Logo from './ui/Logo.vue'

const emit = defineEmits(['authenticated'])

const { login } = useAuth()

const email    = ref('')
const password = ref('')
const busy     = ref(false)
const errorMsg = ref('')

async function handleSubmit() {
  if (busy.value) return
  busy.value = true
  errorMsg.value = ''
  try {
    const user = await login(email.value, password.value)
    emit('authenticated', user)
  } catch (e) {
    errorMsg.value = e.message || 'Error al iniciar sesión'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
/* ─── Page layout ────────────────────────────────────────────────────────────── */
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  font-family: var(--ff-body);
}

.login-wrapper {
  width: 100%;
  max-width: 380px;
  padding: 0 24px;
}

/* ─── Brand / logo area ─────────────────────────────────────────────────────── */
.login-brand {
  text-align: center;
  margin-bottom: 40px;
}

.login-tagline {
  margin: 0;
  font-size: 14px;
  color: var(--text-2);
}

/* ─── Card ───────────────────────────────────────────────────────────────────── */
.login-card {
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  padding: 32px;
  box-shadow: var(--shadow-lg);
}

.login-title {
  margin: 0 0 24px;
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 22px;
  color: var(--text);
  letter-spacing: -0.3px;
}

.login-error {
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 13px;
  color: #dc2626;
  line-height: 1.4;
}

/* ─── Form fields ────────────────────────────────────────────────────────────── */
.form-field {
  margin-bottom: 16px;
}

.form-field--mb-lg {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.form-input {
  display: block;
  width: 100%;
  height: 42px;
  padding: 0 12px;
  background: var(--bg);
  border: 1.5px solid var(--border-strong);
  border-radius: 7px;
  font-family: var(--ff-body);
  font-size: 14px;
  color: var(--text);
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.form-input:focus {
  border-color: var(--primary);
}

/* ─── Submit button ──────────────────────────────────────────────────────────── */
.login-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--ff-head);
  font-weight: 600;
  font-size: 15px;
  transition: background 0.15s;
}

.login-btn--busy {
  background: var(--border-strong);
  cursor: not-allowed;
}

.login-btn__loading {
  opacity: 0.8;
}
</style>
